import AntdInput                  from 'antd/lib/input';
import Space                      from 'antd/lib/space';
import { SearchOutlined }         from '@ant-design/icons';
import Button                     from '@widesk-ui/components/Button';
import React                      from 'react';
import { KeyboardEventHandler }   from 'react';
import { HTMLInputTypeAttribute } from 'react';
import { InputRef }               from 'antd';

export type WideskInputRef = {
	focus: () => void;
	getPastedValue: (value: string) => string | undefined
	selectAll: () => void;
	clear: () => void;
	getValue: () => string | undefined;
};

export type InputProps = FieldProps & {
	value?: string;
	onChange?: (value: string) => void;
	onBlur?: (e: any) => void;
	onClick?: React.MouseEventHandler<HTMLInputElement>;
	onFocus?: (e: any) => void;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
	prefix?: React.ReactNode;
	suffix?: React.ReactNode;
	addonBefore?: React.ReactNode;
	maxLength?: number;
	minLength?: number;
	addonAfter?: React.ReactNode;
	type?: HTMLInputTypeAttribute;
	allowClear?: boolean;
	showCount?: boolean;
	initialValue?: string;
	onPressEnter?: KeyboardEventHandler<HTMLInputElement>;
	pattern?: string;
	className?: string;
	autoComplete?: string;
}

export const Input = React.forwardRef<WideskInputRef, InputProps>((props: InputProps, ref) => {
	const inputRef = React.useRef<InputRef>(null);

	React.useImperativeHandle(ref, () => {
		const ref = inputRef.current;

		return {
			// Retourne la nouvelle valeur de l'input avec la pastedValue incluse au niveau du curseur.
			getPastedValue: (pastedValue) => {
				if (ref && ref.input) {
					const { selectionStart, selectionEnd, value } = ref.input;

					ref.input.focus();

					return `${value.slice(0, selectionStart || 0)}${pastedValue}${value.slice(selectionEnd || 0)}`;
				}
			},
			// Méthode pour focus l'input antd
			focus: () => {
				if (ref && ref.input) {
					setTimeout(() => ref.input?.focus(), 250);
				}
			},
			// Méthode pour sélectionner tout le texte de l'input
			selectAll: () => {
				if (ref && ref.input) {
					ref.input.select();
				}
			},
			clear: () => {
				if (ref && ref.input) {
					const sibling = ref.input.nextElementSibling;
					if (
						sibling?.tagName === 'SPAN'
						&& sibling.classList.contains('ant-input-suffix')
						&& sibling.firstElementChild?.tagName === 'BUTTON'
						&& sibling.firstElementChild.classList.contains('ant-input-clear-icon')
					) {
						const button = sibling.firstElementChild as HTMLButtonElement;
						button.click();
					}
				}
			},
			getValue: () => ref?.input?.value,
		};
	});

	React.useEffect(() => {
		if (props.autoFocus && inputRef.current) {
			setTimeout(inputRef.current.focus, 250);
		}
	}, []);

	const hasAddon = !!props.addonBefore || !!props.addonAfter;

	const input = (
		<AntdInput
			ref={inputRef}
			placeholder={props.placeholder}
			autoFocus={props.autoFocus}
			disabled={props.disabled}
			onBlur={props.onBlur}
			onChange={props.onChange}
			onClick={props.onClick}
			onFocus={props.onFocus}
			onKeyDown={props.onKeyDown}
			onKeyUp={props.onKeyUp}
			size={props.size}
			minLength={props.minLength}
			maxLength={props.maxLength}
			value={props.value}
			formNoValidate={true}
			defaultValue={props.initialValue}
			id={props.id ? `input-${props.id}` : undefined}
			aria-label={props['aria-label']}
			prefix={props.prefix}
			suffix={props.suffix}
			type={props.type}
			allowClear={props.allowClear}
			onPressEnter={props.onPressEnter}
			pattern={props.pattern}
			showCount={props.showCount}
			autoComplete={props.autoComplete}
			{...(hasAddon ? {} : { className: props.className, style: props.style })}
		/>
	);

	if (!hasAddon) {
		return input;
	}

	// Reproduit la structure `ant-input-group` d'Antd (au lieu de passer addonBefore/addonAfter
	// à AntdInput) pour éviter le warning de dépréciation tout en gardant le même rendu.
	return (
		<span
			className={['ant-input-group-wrapper', props.disabled && 'ant-input-group-wrapper-disabled', props.className].filter(Boolean).join(' ')}
			style={props.style}
		>
			<span className="ant-input-wrapper ant-input-group">
				{!!props.addonBefore && <span className="ant-input-group-addon">{props.addonBefore}</span>}
				{input}
				{!!props.addonAfter && <span className="ant-input-group-addon">{props.addonAfter}</span>}
			</span>
		</span>
	);
});

export type InputSearchProps = {
	value?: string;
	placeholder?: string;
	allowClear?: boolean;
	disabled?: boolean;
	loading?: boolean;
	size?: ComponentSize;
	enterButton?: boolean;
	className?: string;
	style?: React.CSSProperties;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onSearch?: (value: string | undefined) => void;
};

// Reconstruit `Input.Search` avec `Space.Compact` : `Input.Search` d'Antd passe toujours son
// bouton via "addonAfter", ce qui déclenche le warning de dépréciation dès qu'il est utilisé.
export const InputSearch = (props: InputSearchProps) => {
	const inputRef = React.useRef<InputRef>(null);

	const triggerSearch = () => {
		props.onSearch?.(inputRef.current?.input?.value || undefined);
	};

	return (
		<Space.Compact className={props.className} style={props.style}>
			<AntdInput
				ref={inputRef}
				value={props.value}
				placeholder={props.placeholder}
				allowClear={props.allowClear}
				disabled={props.disabled}
				size={props.size}
				onChange={props.onChange}
				onPressEnter={triggerSearch}
			/>
			<Button
				icon={<SearchOutlined />}
				type={props.enterButton ? 'primary' : 'default'}
				size={props.size}
				disabled={props.disabled}
				loading={props.loading}
				onClick={triggerSearch}
			/>
		</Space.Compact>
	);
};

export default Input;
